// src/components/mfa/SecurityStatusCard.jsx
//
// What Settings > Security shows about two-step verification. Presentational
// only (props in, callbacks out) so its three states render in tests without a
// browser; SettingsSecurity.jsx owns the data and the dialogs. Same card, tile,
// badge and button styles as BIAccessSection.
import React from 'react';
import {
  AlertCircle, AlertTriangle, CheckCircle2, Clock, Plus, RefreshCw, ShieldCheck, ShieldOff, XCircle,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card.jsx';
import { Badge } from '@/components/ui/badge.jsx';
import { Button } from '@/components/ui/button.jsx';
import { lowOnRecoveryCodes, mfaState, RECOVERY_CODE_COUNT } from '../../lib/mfa';

function StateBadge({ state }) {
  if (state === 'on') {
    return (
      <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
        <CheckCircle2 className="h-3 w-3 mr-1" />
        On
      </Badge>
    );
  }
  if (state === 'pending') {
    return (
      <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">
        <Clock className="h-3 w-3 mr-1" />
        Setup not finished
      </Badge>
    );
  }
  return (
    <Badge className="bg-neutral-500/20 text-neutral-400 border-neutral-500/30">
      <XCircle className="h-3 w-3 mr-1" />
      Off
    </Badge>
  );
}

export default function SecurityStatusCard({ status, error, onSetup, onCancelSetup, onTurnOff, onRegenerate }) {
  const state = mfaState(status);
  const remaining = status?.recovery_codes_remaining ?? 0;

  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-lg bg-blue-500/10 border border-blue-500/30 flex items-center justify-center flex-shrink-0">
              <ShieldCheck className="h-5 w-5 text-blue-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">Two-step verification</h3>
              <p className="text-sm text-[#a0a0a0] mt-0.5">
                Ask for a code from your phone, on top of your password, every time you sign in.
              </p>
            </div>
          </div>
          <StateBadge state={state} />
        </div>

        {error && (
          <div className="flex items-start gap-2 p-3 mb-4 bg-red-500/10 border border-red-500/20 rounded-lg" role="alert">
            <AlertCircle className="h-4 w-4 text-red-400 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-red-400">{error}</p>
          </div>
        )}

        {state === 'off' && (
          <div className="p-4 bg-[#1a1a1a] rounded-lg border border-[#2a2a2a]">
            <p className="text-sm text-[#a0a0a0] mb-3">
              Even if someone learns your password, they cannot sign in without the
              code from your authenticator app. Setup takes about a minute.
            </p>
            <Button onClick={onSetup}>
              <Plus className="h-4 w-4 mr-2" />
              Set up two-step verification
            </Button>
          </div>
        )}

        {state === 'pending' && (
          <div className="p-4 bg-[#1a1a1a] rounded-lg border border-[#2a2a2a]">
            <p className="text-sm text-[#a0a0a0] mb-3">
              You started setting this up but did not enter a code yet, so sign-in
              still asks for your password only. Start again to get a fresh setup key.
            </p>
            <div className="flex gap-2">
              <Button onClick={onSetup}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Start setup again
              </Button>
              <Button variant="ghost" onClick={onCancelSetup}>Cancel setup</Button>
            </div>
          </div>
        )}

        {state === 'on' && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4 p-4 bg-[#1a1a1a] rounded-lg border border-[#2a2a2a]">
              <div>
                <div className="text-xs text-[#a0a0a0] uppercase tracking-wide mb-1">Turned on</div>
                <div className="text-sm text-white">
                  {status.enabled_at ? new Date(status.enabled_at).toLocaleString() : 'Yes'}
                </div>
              </div>
              <div>
                <div className="text-xs text-[#a0a0a0] uppercase tracking-wide mb-1">Recovery codes left</div>
                <div className="text-sm text-white">{remaining} of {RECOVERY_CODE_COUNT}</div>
              </div>
            </div>

            {lowOnRecoveryCodes(remaining) && (
              <div className="flex items-start gap-2 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                <AlertTriangle className="h-4 w-4 text-amber-400 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-amber-200">
                  {remaining === 0
                    ? 'You have no recovery codes left. Make new ones so a lost phone does not lock you out.'
                    : `Only ${remaining} recovery ${remaining === 1 ? 'code' : 'codes'} left. Make a new set soon.`}
                </p>
              </div>
            )}

            <div className="flex gap-2">
              <Button variant="outline" onClick={onRegenerate}>
                <RefreshCw className="h-4 w-4 mr-2" />
                New recovery codes
              </Button>
              <Button
                variant="ghost"
                onClick={onTurnOff}
                className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
              >
                <ShieldOff className="h-4 w-4 mr-2" />
                Turn off
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
